<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import AutomationFileInput from 'dashboard/components/widgets/AutomationFileInput.vue';

const MAX_CANNED_ATTACHMENTS = 15; // espelha Message::NUMBER_OF_PERMITTED_ATTACHMENTS
const ACCEPTED_TYPES =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.csv,.xls,.xlsx,.txt';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    AutomationFileInput,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      existingAttachments: [...this.edattachments],
      attachedBlobIds: this.edattachments.map(a => a.blob_id),
      maxAttachments: MAX_CANNED_ATTACHMENTS,
      acceptedTypes: ACCEPTED_TYPES,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    onAttachmentsUploaded(ids) {
      this.attachedBlobIds = [...this.attachedBlobIds, ...ids].slice(
        0,
        this.maxAttachments
      );
    },
    removeAttachment(blobId) {
      this.attachedBlobIds = this.attachedBlobIds.filter(id => id !== blobId);
      this.existingAttachments = this.existingAttachments.filter(
        a => a.blob_id !== blobId
      );
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          blob_ids: this.attachedBlobIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full mt-2">
          <label>{{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.LABEL') }}</label>
          <ul
            v-if="existingAttachments.length"
            class="mb-2 flex flex-col gap-1"
          >
            <li
              v-for="attachment in existingAttachments"
              :key="attachment.blob_id"
              class="flex items-center justify-between text-xs"
            >
              <span>{{ attachment.filename || attachment.blob_id }}</span>
              <NextButton
                xs
                ghost
                ruby
                icon="i-lucide-trash-2"
                @click.prevent="removeAttachment(attachment.blob_id)"
              />
            </li>
          </ul>
          <AutomationFileInput
            v-if="attachedBlobIds.length < maxAttachments"
            upload-action="uploadAttachment"
            :accept="acceptedTypes"
            multiple
            @update:model-value="onAttachmentsUploaded"
          />
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
